/**
 * DireccionesSection — fiscal, origen y devoluciones (MVP-02C §3.3, revisión).
 *
 * `CompanyAddress` guarda `city`/`province`/`postal_code` como texto (no FK):
 * es una identidad de empresa versionada por snapshot, no una dirección de
 * cliente enlazada a los geodatos. Pero el formulario reutiliza el mismo
 * selector de provincia/municipio/CP de MVP-02B (`useDireccionForm` +
 * `DireccionFormFields`, sección "location"), así que municipio y provincia
 * nunca son texto libre: se eligen de una lista real y el backend además
 * revalida la combinación contra los geodatos antes de guardar.
 */
import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
    AlertTriangle, CheckCircle2, Copy, Plus, RotateCw,
} from "lucide-react";

import { TextInput, showErrorAlert, confirmDelete } from "../../../../../components/ui/index.js";
import { DireccionFormFields, useDireccionForm } from "../../../../../domain/direcciones/index.js";
import {
    activateAddress, createAddress, fetchAddresses, parseAdminErrors,
} from "../../../../../domain/shipping-admin/api.js";

const KIND_LABEL = {
    fiscal: "Domicilio fiscal",
    origin: "Origen / remitente",
    returns: "Devoluciones",
};

const KINDS = ["origin", "fiscal", "returns"];

const COMPANY_FIELDS_VACIO = {
    label: "", company_name: "", contact_name: "", tax_id: "",
    address_line1: "", address_line2: "", phone: "", email: "",
};

// Traduce las claves del error del backend (forma de CompanyAddress) a las
// claves que `DireccionFormFields` sabe mostrar (forma compartida de MVP-02B).
const GEO_ERROR_KEY = { city: "municipio", province: "provincia", postal_code: "codigo_postal" };

const formatFecha = (valor) =>
    valor ? new Date(valor).toLocaleDateString("es-ES", { dateStyle: "medium" }) : "—";

function AddressForm({ kind, onCreated }) {
    const [company, setCompany] = useState(COMPANY_FIELDS_VACIO);
    const [companyErrors, setCompanyErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(false);
    const geoForm = useDireccionForm();

    const setCompanyField = (field, v) => {
        setCompany((prev) => ({ ...prev, [field]: v }));
        if (companyErrors[field]) setCompanyErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const validate = () => {
        const errors = {};
        if (!company.address_line1.trim()) errors.address_line1 = "La calle y número son obligatorios.";
        setCompanyErrors(errors);

        const geoErrors = {};
        if (!geoForm.value.provincia) geoErrors.provincia = "Selecciona una provincia.";
        if (!geoForm.value.municipio) geoErrors.municipio = "Selecciona un municipio.";
        if (!/^\d{5}$/.test(geoForm.value.codigo_postal ?? "")) {
            geoErrors.codigo_postal = "El código postal debe tener 5 dígitos.";
        }
        geoForm.setErrors(geoErrors);

        return Object.keys(errors).length === 0 && Object.keys(geoErrors).length === 0;
    };

    const submit = async (event) => {
        event.preventDefault();
        if (!validate()) return;
        setSaving(true);
        try {
            const provinciaNombre = geoForm.geografia.provincias.data.find(
                (p) => Number(p.id) === Number(geoForm.value.provincia),
            )?.nombre ?? "";
            const municipioNombre = geoForm.municipioSeleccionado?.nombre ?? "";

            await createAddress({
                ...company,
                kind,
                city: municipioNombre,
                province: provinciaNombre,
                postal_code: geoForm.value.codigo_postal,
                country: "España",
            });
            setCompany(COMPANY_FIELDS_VACIO);
            geoForm.reset();
            setOpen(false);
            setCompanyErrors({});
            onCreated();
        } catch (error) {
            const backendErrors = parseAdminErrors(error);
            const nextCompanyErrors = {};
            const nextGeoErrors = {};
            for (const [key, message] of Object.entries(backendErrors)) {
                if (GEO_ERROR_KEY[key]) nextGeoErrors[GEO_ERROR_KEY[key]] = message;
                else nextCompanyErrors[key] = message;
            }
            setCompanyErrors(nextCompanyErrors);
            geoForm.setErrors((prev) => ({ ...prev, ...nextGeoErrors }));
        } finally {
            setSaving(false);
        }
    };

    if (!open) {
        return (
            <button type="button" onClick={() => setOpen(true)} className="btn btn-outline btn-sm gap-1 self-start">
                <Plus className="w-4 h-4" /> Nueva versión de {KIND_LABEL[kind].toLowerCase()}
            </button>
        );
    }

    return (
        <form onSubmit={submit} className="rounded-xl border border-base-300 bg-base-200 p-4 flex flex-col gap-4">
            <h3 className="font-semibold text-sm">Nueva versión — {KIND_LABEL[kind]}</h3>
            {companyErrors.general && <p role="alert" className="text-sm text-error">{companyErrors.general}</p>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Campo id={`${kind}-label`} label="Etiqueta interna" error={companyErrors.label}>
                    <TextInput id={`${kind}-label`} value={company.label} disabled={saving}
                        onChange={(e) => setCompanyField("label", e.target.value)}
                        placeholder="Almacén principal" />
                </Campo>
                <Campo id={`${kind}-company`} label="Razón social" error={companyErrors.company_name}>
                    <TextInput id={`${kind}-company`} value={company.company_name} disabled={saving}
                        onChange={(e) => setCompanyField("company_name", e.target.value)} />
                </Campo>
                <Campo id={`${kind}-contact`} label="Persona de contacto" error={companyErrors.contact_name}>
                    <TextInput id={`${kind}-contact`} value={company.contact_name} disabled={saving}
                        onChange={(e) => setCompanyField("contact_name", e.target.value)} />
                </Campo>
                <Campo id={`${kind}-tax`} label="NIF / CIF" error={companyErrors.tax_id}>
                    <TextInput id={`${kind}-tax`} value={company.tax_id} disabled={saving}
                        onChange={(e) => setCompanyField("tax_id", e.target.value)} />
                </Campo>
                <Campo id={`${kind}-line1`} label="Calle y número" requerido error={companyErrors.address_line1}>
                    <TextInput id={`${kind}-line1`} value={company.address_line1} disabled={saving}
                        onChange={(e) => setCompanyField("address_line1", e.target.value)} />
                </Campo>
                <Campo id={`${kind}-line2`} label="Piso, puerta, etc." error={companyErrors.address_line2}>
                    <TextInput id={`${kind}-line2`} value={company.address_line2} disabled={saving}
                        onChange={(e) => setCompanyField("address_line2", e.target.value)} />
                </Campo>
                <Campo id={`${kind}-phone`} label="Teléfono" error={companyErrors.phone}>
                    <TextInput id={`${kind}-phone`} type="tel" value={company.phone} disabled={saving}
                        onChange={(e) => setCompanyField("phone", e.target.value)} />
                </Campo>
                <Campo id={`${kind}-email`} label="Email" error={companyErrors.email}>
                    <TextInput id={`${kind}-email`} type="email" value={company.email} disabled={saving}
                        onChange={(e) => setCompanyField("email", e.target.value)} />
                </Campo>
            </div>

            {/* Provincia / municipio / CP: mismo selector y misma validación que
                Perfil y Checkout (MVP-02B), no texto libre. */}
            <DireccionFormFields
                form={geoForm}
                variant="checkout"
                disabled={saving}
                sections={["location"]}
                showDefaultToggle={false}
            />

            {kind === "origin" && (
                <p className="text-xs text-base-content/60 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    El origen debe quedar en España peninsular: Baleares, Canarias, Ceuta y
                    Melilla no se pueden activar como remitente.
                </p>
            )}

            <div className="flex gap-2">
                <button type="submit" className="btn btn-sm btn-primary" disabled={saving}>
                    {saving ? "Guardando…" : "Guardar como borrador"}
                </button>
                <button type="button" className="btn btn-sm btn-ghost" disabled={saving} onClick={() => setOpen(false)}>
                    Cancelar
                </button>
            </div>
        </form>
    );
}

AddressForm.propTypes = { kind: PropTypes.string.isRequired, onCreated: PropTypes.func.isRequired };

const Campo = ({ id, label, requerido, error, children }) => (
    <div className="form-control gap-1">
        <label className="label py-0" htmlFor={id}>
            <span className="label-text text-xs font-semibold">
                {label}{requerido && <span className="text-error"> *</span>}
            </span>
        </label>
        {children}
        {error && <span role="alert" className="label-text-alt text-error">{error}</span>}
    </div>
);
Campo.propTypes = {
    id: PropTypes.string.isRequired, label: PropTypes.string.isRequired,
    requerido: PropTypes.bool, error: PropTypes.string, children: PropTypes.node,
};

function KindGroup({ kind, addresses, onChanged }) {
    const [activating, setActivating] = useState(null);
    const list = addresses.filter((a) => a.kind === kind);
    const active = list.find((a) => a.is_active);

    const activate = async (address) => {
        const ok = await confirmDelete({
            title: `¿Activar esta versión de ${KIND_LABEL[kind].toLowerCase()}?`,
            text: active
                ? `Sustituirá a la versión actualmente activa (${active.address_line1}, ${active.city}). El checkout usará esta dirección de inmediato.`
                : "El checkout usará esta dirección de inmediato.",
            confirmButtonText: "Activar",
        });
        if (!ok) return;

        setActivating(address.id);
        try {
            await activateAddress(address.id);
            onChanged();
        } catch (error) {
            const parsed = parseAdminErrors(error);
            showErrorAlert({
                text: parsed.detail || parsed.general || Object.values(parsed)[0]
                    || "No se pudo activar la dirección.",
            });
        } finally {
            setActivating(null);
        }
    };

    return (
        <section className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">
                    {KIND_LABEL[kind]}
                    {active ? (
                        <span className="badge badge-success badge-sm gap-1">
                            <CheckCircle2 className="w-3 h-3" /> Activa
                        </span>
                    ) : (
                        <span className="badge badge-ghost badge-sm">Sin activar</span>
                    )}
                </h3>
            </div>

            {list.length === 0 ? (
                <p className="text-sm text-base-content/50">Todavía no hay ninguna versión.</p>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-base-300">
                    <table className="table table-sm table-zebra">
                        <thead>
                            <tr>
                                <th>Etiqueta</th><th>Dirección</th><th>Estado</th><th>Creada</th><th />
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((address) => (
                                <tr key={address.id}>
                                    <td>{address.label || "—"}</td>
                                    <td className="text-xs">
                                        {address.address_line1}, {address.city}
                                        {address.province && ` (${address.province})`} · {address.postal_code}
                                    </td>
                                    <td>
                                        {address.is_active
                                            ? <span className="badge badge-success badge-sm">Activa</span>
                                            : <span className="badge badge-ghost badge-sm">Histórica</span>}
                                    </td>
                                    <td className="text-xs text-base-content/60">{formatFecha(address.created_at)}</td>
                                    <td className="text-right">
                                        {!address.is_active && (
                                            <button
                                                type="button"
                                                className="btn btn-xs btn-outline"
                                                disabled={activating === address.id}
                                                onClick={() => activate(address)}
                                            >
                                                {activating === address.id ? "Activando…" : "Activar"}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <AddressForm kind={kind} onCreated={onChanged} />
        </section>
    );
}
KindGroup.propTypes = {
    kind: PropTypes.string.isRequired,
    addresses: PropTypes.array.isRequired,
    onChanged: PropTypes.func.isRequired,
};

export default function DireccionesSection({ onChanged }) {
    const [recurso, setRecurso] = useState({ state: "loading", data: [] });

    const cargar = useCallback(() => {
        setRecurso((prev) => ({ ...prev, state: "loading" }));
        return fetchAddresses()
            .then((data) => setRecurso({ state: "ready", data }))
            .catch(() => setRecurso({ state: "error", data: [] }));
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const handleChanged = () => {
        cargar();
        onChanged();
    };

    if (recurso.state === "loading") {
        return (
            <div className="flex flex-col gap-2" aria-busy="true">
                <div className="h-24 rounded-lg bg-base-300/60 animate-pulse" />
                <div className="h-24 rounded-lg bg-base-300/60 animate-pulse" />
                <span className="sr-only">Cargando direcciones…</span>
            </div>
        );
    }

    if (recurso.state === "error") {
        return (
            <div role="alert" className="flex flex-col items-start gap-3 py-6">
                <p className="text-sm text-error">No se pudieron cargar las direcciones.</p>
                <button type="button" className="btn btn-sm gap-1" onClick={cargar}>
                    <RotateCw className="w-4 h-4" /> Reintentar
                </button>
            </div>
        );
    }

    return (
        <div className="rounded-xl bg-base-100 border border-base-300 shadow-sm p-4 flex flex-col gap-8">
            <p className="text-xs text-base-content/60 flex items-center gap-1.5">
                <Copy className="w-3.5 h-3.5 shrink-0" />
                Fiscal, origen y devoluciones son identidades separadas: copiar datos entre
                ellas es una acción explícita (rellenar el formulario), nunca automática.
            </p>
            {KINDS.map((kind) => (
                <KindGroup key={kind} kind={kind} addresses={recurso.data} onChanged={handleChanged} />
            ))}
        </div>
    );
}

DireccionesSection.propTypes = { onChanged: PropTypes.func.isRequired };
